import { FileType, MediaChannel } from '@nimbus-global/features/manage/model/file-type-policy.enum';

export enum TwilioMessageStatus {
  None = 'None',
  Undelivered = 'Undelivered',
  Pending = 'Pending',
  Failed = 'Failed',
  Sent = 'Sent',
  Delivered = 'Delivered',
  Read = 'Read',
}

export const TwilioMessageStatusPriority: TwilioMessageStatus[] = [
  TwilioMessageStatus.None,
  TwilioMessageStatus.Pending,
  TwilioMessageStatus.Sent,
  TwilioMessageStatus.Delivered,
  TwilioMessageStatus.Read,
  TwilioMessageStatus.Failed,
  TwilioMessageStatus.Undelivered,
];

export function getUpdatedTwilioStatus(currentStatus: TwilioMessageStatus, newStatus: TwilioMessageStatus): TwilioMessageStatus {
  const safeCurrentStatus = currentStatus || TwilioMessageStatus.None;
  const safeNewStatus = newStatus || TwilioMessageStatus.None;
  const isNewBehind = TwilioMessageStatusPriority.indexOf(safeNewStatus) < TwilioMessageStatusPriority.indexOf(safeCurrentStatus);

  return isNewBehind ? safeCurrentStatus : safeNewStatus;
}

export interface TwilioMessageRequest {
  author: string;
  body: string;
  dateCreated: string;
}

export interface LastMessageIndexRequest {
  index: number;
}

export enum MediaBlockReason {
  None = 'None',
  NoPolicyAssigned = 'NoPolicyAssigned',
  UnknownFileType = 'UnknownFileType',
  FileTypeNotAllowed = 'FileTypeNotAllowed',
  ContentMismatch = 'ContentMismatch',
  ChannelNotAllowed = 'ChannelNotAllowed',
  TooLarge = 'TooLarge',
  PolicyUnavailable = 'PolicyUnavailable',
}

export interface TwilioMessageMedia {
  url: string;
  contentType: string | null;
  filename: string | null;
  size: number | null;
  isBlocked?: boolean;
  blockReason?: MediaBlockReason;
}

export interface IMediaSendErrorResponse {
  filename?: string;
  blockReason?: MediaBlockReason;
  maxSizeBytes?: number | null;
}

export enum MessageDeliveryFailureReason {
  None = 'None',
  Unknown = 'Unknown',
  UnsupportedMediaContent = 'UnsupportedMediaContent',
  ChannelPolicyViolation = 'ChannelPolicyViolation',
  MessagingWindowExpired = 'MessagingWindowExpired',
  RateLimited = 'RateLimited',
  RecipientUnreachable = 'RecipientUnreachable',
  MessageFiltered = 'MessageFiltered',
}

export interface TwilioMessage extends TwilioMessageRequest {
  messageSid: string;
  index: number;
  deliveryState: TwilioMessageStatus;
  media?: TwilioMessageMedia[] | null;
  errorCode?: number | null;
  failureReason?: MessageDeliveryFailureReason | null;
}

export interface TwilioLiveMessage extends TwilioMessage {
  conversationId: string;
}

export interface TwilioMessageStatusUpdate {
  messageSid: string;
  status: TwilioMessageStatus;
  conversationId: string;
  errorCode?: number | null;
  failureReason?: MessageDeliveryFailureReason | null;
}

export interface ITwilioMessageResponse {
  messages: TwilioMessage[];
  draft: IChatDraftMessage;
}

export interface IChatDraftMessage {
  body: string;
}

export interface TwilioUrlParams {
  tenantId: string;
  conversationId: string;
  userId: string;
  limit?: number;
}

export enum ConversationMediaPolicyStatus {
  Available = 'Available',
  NoPolicyAssigned = 'NoPolicyAssigned',
  ChannelNotSupported = 'ChannelNotSupported',
}

export interface IConversationMediaAllowedFileType {
  fileType: FileType;
  displayName: string;
  mimeTypes: string[];
  extensions: string[];
  maxSizeBytes: number;
}

export interface IConversationMediaPolicy {
  status: ConversationMediaPolicyStatus;
  channel: MediaChannel;
  channelSizeLimitBytes: number;
  allowedFileTypes: IConversationMediaAllowedFileType[];
}
